import { v4 as uuid } from 'uuid'

// The admin form edits a single unified list of detail-panel rows where each
// row carries a `scope` indicating whether it renders in the item panel, the
// category panel, or both. Persistence remains two parallel arrays
// (`itemMetadata`, `categoryMetadata`) on the component config, so the
// renderer contract and back-compat behavior are unchanged. These helpers
// translate between the unified view (what the editor manipulates) and the
// stored shape (what the renderer reads).

export const SCOPE = Object.freeze({
  ITEM: 'item',
  CATEGORY: 'category',
  BOTH: 'both'
})

function ensureRowId (row) {
  if (!row || typeof row !== 'object') return null
  return row.id ? row : { ...row, id: uuid() }
}

function stripScope (row) {
  if (!row) return row
  const { scope, ...rest } = row
  return rest
}

function rowsEqualByContent (a, b) {
  if (!a || !b) return false
  return a.accessor === b.accessor && a.label === b.label
}

// Merge the two persisted arrays into a single unified list. Rows that appear
// in both arrays collapse into one entry with `scope: 'both'`. Matching is by
// `id` first (the common case once admins re-save after the unified editor
// ships); we fall back to `accessor + label` to bridge historical data where
// the same row was duplicated in each array with different generated ids.
//
// Order: item-scoped rows render first (preserving the legacy item order),
// then category-only rows append in their original order. Rows promoted to
// `both` keep the item position so the unified list mirrors the more common
// "items panel" view.
export function mergeMetadataScopes (itemMetadata, categoryMetadata) {
  const itemRows = (Array.isArray(itemMetadata) ? itemMetadata : []).map(ensureRowId).filter(Boolean)
  const categoryRows = (Array.isArray(categoryMetadata) ? categoryMetadata : []).map(ensureRowId).filter(Boolean)

  const consumedCategoryIndexes = new Set()
  const unified = []

  itemRows.forEach((itemRow) => {
    let matchIndex = categoryRows.findIndex((catRow, idx) => !consumedCategoryIndexes.has(idx) && catRow.id === itemRow.id)
    if (matchIndex === -1) {
      matchIndex = categoryRows.findIndex((catRow, idx) => !consumedCategoryIndexes.has(idx) && rowsEqualByContent(catRow, itemRow))
    }
    if (matchIndex !== -1) {
      consumedCategoryIndexes.add(matchIndex)
      unified.push({ ...itemRow, scope: SCOPE.BOTH })
    } else {
      unified.push({ ...itemRow, scope: SCOPE.ITEM })
    }
  })

  categoryRows.forEach((catRow, idx) => {
    if (consumedCategoryIndexes.has(idx)) return
    unified.push({ ...catRow, scope: SCOPE.CATEGORY })
  })

  return unified
}

// Split a unified list back into the two persisted arrays. Rows scoped to a
// kind appear in that array in their unified order; rows scoped to `both`
// appear in both arrays at their corresponding position. This preserves the
// renderer's existing contract (it still reads `itemMetadata` and
// `categoryMetadata` independently) while the editor presents a single list.
export function splitMetadataScopes (unified) {
  const itemRows = []
  const categoryRows = []
  if (!Array.isArray(unified)) {
    return { itemMetadata: itemRows, categoryMetadata: categoryRows }
  }

  unified.forEach((row) => {
    if (!row) return
    const stripped = stripScope(row)
    const scope = row.scope || SCOPE.BOTH
    if (scope === SCOPE.ITEM || scope === SCOPE.BOTH) itemRows.push(stripped)
    if (scope === SCOPE.CATEGORY || scope === SCOPE.BOTH) categoryRows.push(stripped)
  })

  return { itemMetadata: itemRows, categoryMetadata: categoryRows }
}

// Toggling a single side of the Show On checkboxes. Refuses to clear both
// sides — the trash icon is the explicit way to remove a row, so we always
// keep at least one scope checked to avoid silently dropping the row when the
// next save splits the unified list back into the two arrays.
export function toggleRowScope (currentScope, side, checked) {
  const showsItem = currentScope === SCOPE.ITEM || currentScope === SCOPE.BOTH
  const showsCategory = currentScope === SCOPE.CATEGORY || currentScope === SCOPE.BOTH

  let nextShowsItem = side === SCOPE.ITEM ? checked : showsItem
  let nextShowsCategory = side === SCOPE.CATEGORY ? checked : showsCategory

  if (!nextShowsItem && !nextShowsCategory) {
    if (side === SCOPE.ITEM) nextShowsCategory = true
    else nextShowsItem = true
  }

  if (nextShowsItem && nextShowsCategory) return SCOPE.BOTH
  if (nextShowsItem) return SCOPE.ITEM
  return SCOPE.CATEGORY
}
